/**
 * Collection Filters JavaScript
 * Handles filter form submission and UI interactions for collection pages
 */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.i18n = (window.SHOPIFY_CONFIG && window.SHOPIFY_CONFIG.translations) || {};
    this.requestClient = window.themeRequest || {
      fetchText: function(url, options) {
        return fetch(url, options).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        });
      }
    };
    this.filterData = [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.currentRequestId = 0;
    this.activeController = null;
    this.retryHandler = null;
  }

  connectedCallback() {
    this.filterData = [...this.querySelectorAll('input, select')].map((element) => ({
      type: element.type,
      name: element.name,
      value: element.value
    }));

    // Handle form input changes (including selects in older browsers).
    this.querySelector('form').addEventListener('input', this.onFormSubmission.bind(this));
    this.querySelector('form').addEventListener('change', this.onFormSubmission.bind(this));
    
    // Handle active filter removal
    this.querySelectorAll('.facets__active-filter').forEach((activeFilter) => {
      activeFilter.addEventListener('click', this.onActiveFilterClick);
    });

    // Handle clear all functionality
    const clearAllButton = this.querySelector('.facets__reset');
    if (clearAllButton) {
      clearAllButton.addEventListener('click', this.onClearAll.bind(this));
    }

    this.applyDataStyles(this);
    this.bindPaginationLinks();
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    if (this.activeController) {
      this.activeController.abort();
      this.activeController = null;
    }
  }

  onFormSubmission(event) {
    if (!event.target.closest('form')) return;
    this.updateFilters(event.target);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    this.toggleFilter(event.currentTarget);
  }

  onClearAll(event) {
    event.preventDefault();
    this.clearAllFilters();
  }

  updateFilters(target) {
    void target;
    // Debounce the form submission to avoid too many requests
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.submitForm();
    }, 300);
  }

  submitForm() {
    const form = this.querySelector('form');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    const newURL = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    this.renderFromUrl(newURL, true);
  }

  toggleFilter(element) {
    const url = element.getAttribute('href');
    if (url) {
      this.renderFromUrl(url, true);
    }
  }

  clearAllFilters() {
    const collectionUrl = window.location.pathname;
    this.renderFromUrl(collectionUrl, true);
  }

  onPopState() {
    this.renderFromUrl(window.location.href, false);
  }

  bindPaginationLinks() {
    const contentArea = document.getElementById('collection-content-area');
    if (!contentArea) return;

    contentArea.querySelectorAll('nav[aria-label="Pagination"] a').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href) return;
        event.preventDefault();
        this.renderFromUrl(href, true);
      });
    });
  }

  ensureErrorContainer() {
    const contentArea = document.getElementById('collection-content-area');
    if (!contentArea) return null;
    let element = contentArea.querySelector('[data-filters-error]');
    if (element) return element;

    element = document.createElement('div');
    element.className = 'hidden mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700';
    element.setAttribute('data-filters-error', 'true');
    const retryLabel = this.i18n.retryLabel || 'Retry';
    element.innerHTML = `<span data-role="message"></span> <button type="button" class="ml-2 underline font-medium" data-role="retry">${retryLabel}</button>`;
    contentArea.prepend(element);
    return element;
  }

  showError(message, retryHandler) {
    const container = this.ensureErrorContainer();
    if (!container) return;
    this.retryHandler = typeof retryHandler === 'function' ? retryHandler : null;
    const messageNode = container.querySelector('[data-role="message"]');
    const retryButton = container.querySelector('[data-role="retry"]');
    if (messageNode) {
      messageNode.textContent = message || this.i18n.filtersApplyError || 'Unable to update filters right now.';
    }
    if (retryButton) {
      retryButton.classList.toggle('hidden', !this.retryHandler);
      retryButton.disabled = !this.retryHandler;
    }
    container.classList.remove('hidden');
  }

  clearError() {
    const contentArea = document.getElementById('collection-content-area');
    if (!contentArea) return;
    const container = contentArea.querySelector('[data-filters-error]');
    if (!container) return;
    this.retryHandler = null;
    container.classList.add('hidden');
  }

  applyDataStyles(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-swatch-color]').forEach((element) => {
      const color = element.getAttribute('data-swatch-color');
      if (color) {
        element.style.backgroundColor = color;
      }
    });
  }

  renderFromUrl(url, updateHistory) {
    const nextUrl = new URL(url, window.location.origin);
    const contentArea = document.getElementById('collection-content-area');
    if (!contentArea) {
      if (updateHistory) {
        window.location.assign(nextUrl.toString());
      } else {
        window.location.replace(nextUrl.toString());
      }
      return;
    }

    const requestId = ++this.currentRequestId;
    if (this.activeController) {
      this.activeController.abort();
    }
    this.activeController = new AbortController();

    this.requestClient.fetchText(nextUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this.activeController.signal,
      retry: 1,
      timeoutMs: 12000
    })
      .then((html) => {
        if (requestId !== this.currentRequestId) return;
        this.clearError();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const updatedContentArea = doc.getElementById('collection-content-area');
        if (!updatedContentArea) {
          window.location.assign(nextUrl.toString());
          return;
        }

        contentArea.innerHTML = updatedContentArea.innerHTML;
        if (updateHistory && window.location.href !== nextUrl.toString()) {
          window.history.pushState({ path: nextUrl.toString() }, '', nextUrl.toString());
        }
        this.searchParamsPrev = nextUrl.search.slice(1);
        this.applyDataStyles(contentArea);
        this.bindPaginationLinks();
      })
      .catch((error) => {
        if (error.kind === 'aborted' || error.name === 'AbortError') return;
        this.showError(this.i18n.filtersApplyError || 'Could not apply filters. Please try again.', () => {
          this.renderFromUrl(nextUrl.toString(), updateHistory);
        });
      });
  }
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const url = event.currentTarget.getAttribute('href');
      const filtersForm = this.closest('facet-filters-form');
      if (filtersForm && typeof filtersForm.renderFromUrl === 'function') {
        filtersForm.renderFromUrl(url, true);
        return;
      }
      window.location.assign(url);
    });
  }
}

// Define custom elements
customElements.define('facet-filters-form', FacetFiltersForm);
customElements.define('facet-remove', FacetRemove);

// Initialize filters when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Handle mobile filter toggle
  const mobileToggle = document.querySelector('.facets__mobile-toggle details');
  if (mobileToggle) {
    mobileToggle.addEventListener('toggle', function() {
      if (this.open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }

  // Handle filter disclosure states
  const filterDisclosures = document.querySelectorAll('.facets__disclosure');
  filterDisclosures.forEach(disclosure => {
    const summary = disclosure.querySelector('.facets__summary');
    const icon = summary.querySelector('.icon-caret');
    
    disclosure.addEventListener('toggle', function() {
      if (icon) {
        icon.style.transform = this.open ? 'rotate(90deg)' : 'rotate(0deg)';
      }
    });
  });

  document.addEventListener('click', function(event) {
    const retryButton = event.target.closest('[data-filters-error] [data-role="retry"]');
    if (!retryButton) return;
    const form = document.querySelector('facet-filters-form');
    if (form && typeof form.retryHandler === 'function') {
      event.preventDefault();
      form.retryHandler();
    }
  });
});
